/**
 * VoyagePlansStep — admin UI for managing the per-vessel Voyage Plan library.
 *
 * Mounted inside VoyageSettingsModal between Operation Types and Voyage Purposes.
 *
 * Persistence: HOOK-DIRECT auto-save via useVoyagePlans (every edit writes to
 * voyage_settings.voyagePlans[] immediately). See CODING-BRIEF-A2.md §10 for the
 * justification of HOOK-DIRECT over BUFFERED — the existing
 * OperationTemplateEditor uses BUFFERED, but useVoyagePlans is designed around
 * direct writes and the §A2-FIX-1 merge semantics depend on hook writes being
 * persisted between modal-open and modal-Save-click.
 *
 * Render-zero discipline (P4.5): EVERY numeric short-circuit uses !!() or
 * ternary. Never `{count && jsx}` — the POB hotfix cc4932a (2026-06-07) was
 * caused by that exact pattern.
 *
 * P4.6 forward-compat: admin-side label resolution reads live operationsList.
 * Master flows (Underway / ArrivalPhase / PDF / audit log) MUST read from
 * voyage.planSnapshot instead — see comment at the resolution site.
 *
 * P4.9 PlanItem.kind lock: UI ONLY offers 'waypoint' and 'operation_ref'.
 * OperationRefPicker excludes phaseType ops AND id === 'pre_arrival' AND
 * disabled ops (belt-and-braces).
 *
 * @component
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import useVoyagePlans from '../../../../hooks/domain/operations/useVoyagePlans';
import formatEditedAgo from '../../../../utils/formatEditedAgo';
import Dropdown from '../../../common/Dropdown';

// --- Module-scope constants (declared BEFORE the component to avoid TDZ) ---

const EMPTY_STATE_HEADLINE = 'No plans yet';
// LOCKED verbatim from synthesis B8 — including the deliberate "harbour
// yokohama placement" quirk. Do NOT "correct" the casing or wording.
const EMPTY_STATE_BODY =
  'Voyage Plans are recipes for the voyages this vessel does regularly. Build one for each known route or job (e.g. Belfast → Bangor, harbour yokohama placement).';
const EMPTY_STATE_CTA = 'Create your first plan';

// --- Empty-state subcomponent ---

const EmptyState = ({ onCreate }) => (
  <div className="p-8 text-center">
    <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{EMPTY_STATE_HEADLINE}</h3>
    <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">{EMPTY_STATE_BODY}</p>
    <button
      type="button"
      onClick={onCreate}
      className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-md text-sm font-medium hover:bg-cyan-700 min-h-[44px]"
    >
      <Plus className="h-4 w-4" />
      {EMPTY_STATE_CTA}
    </button>
  </div>
);

// --- OperationRefPicker — flat alphabetical type-ahead (per CORRECTION 7) ---

const OperationRefPicker = ({ voyageSettings, onPick, onClose }) => {
  const [query, setQuery] = useState('');

  const operations = useMemo(() => {
    const list = Array.isArray(voyageSettings?.operationsList)
      ? voyageSettings.operationsList
      : [];
    return list
      .filter((op) => !op.phaseType) // exclude phase ops (pre_departure/pre_arrival/post_arrival)
      .filter((op) => op.enabled !== false) // exclude explicitly-disabled ops
      .filter((op) => op.id !== 'pre_arrival') // belt-and-braces P4.9
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || '')); // CORRECTION 7: alphabetical
  }, [voyageSettings]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return operations;
    return operations.filter((op) => {
      const labelMatch = (op.label || '').toLowerCase().includes(q);
      const descMatch = (op.description || '').toLowerCase().includes(q);
      return labelMatch || descMatch;
    });
  }, [operations, query]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-base font-semibold mb-2">Add operation reference</h3>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search operations…"
            autoFocus
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md min-h-[44px] focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        <div className="overflow-y-auto p-2 flex-1">
          {operations.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 italic">
              No operations defined for this vessel. Add operations in the Operation Types section above first.
            </p>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 italic">No matches.</p>
          ) : (
            <ul>
              {filtered.map((op) => (
                <li key={op.id}>
                  <button
                    type="button"
                    onClick={() => onPick(op.id)}
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 min-h-[44px]"
                  >
                    {op.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-3 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 min-h-[44px]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// --- PlanItemRow ---

const PlanItemRow = ({
  item,
  position,
  isFirst,
  isLast,
  voyageSettings,
  onUpdate,
  onMoveUp,
  onMoveDown,
  onRemove,
}) => {
  const isWaypoint = item.kind === 'waypoint';

  // ADMIN-ONLY label resolution: reads live voyageSettings.operationsList.
  // DO NOT copy this pattern to Master flows (Underway plan card, ArrivalPhase,
  // Voyage detail, PDF export, audit log). Those MUST read from voyage.planSnapshot
  // to honour the regulatory invariant (P4.6, DESIGN-SYNTHESIS-v4-FINAL.md §6.6).
  // Reason: admin is by definition editing the library, so live read is correct here;
  // Master flows must see what was promised at Let Go regardless of subsequent edits.
  const operation = !isWaypoint
    ? (Array.isArray(voyageSettings?.operationsList) ? voyageSettings.operationsList : [])
        .find((op) => op.id === item.operationId)
    : null;
  const operationMissing = !isWaypoint && operation === undefined;
  const resolvedOperationLabel = !isWaypoint
    ? (item.displayLabel || operation?.label || `(Operation not found: ${item.operationId})`)
    : '';

  return (
    <li
      className={`flex items-start gap-2 p-2 rounded-md min-h-[56px] ${
        isWaypoint ? 'bg-cyan-50' : 'bg-violet-50'
      }`}
    >
      {/* Position number + up/down arrows */}
      <div className="flex flex-col items-center gap-1 pt-1 flex-shrink-0">
        <span className="text-xs font-medium text-gray-600 tabular-nums">{position}</span>
        <div className="flex flex-col">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            aria-label={`Move item ${position} up`}
            className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-cyan-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            aria-label={`Move item ${position} down`}
            className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-cyan-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      {isWaypoint ? (
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-center gap-1 mb-1">
            <MapPin className="h-4 w-4 text-cyan-600 flex-shrink-0" />
            <input
              type="text"
              value={item.label || ''}
              onChange={(e) => onUpdate({ label: e.target.value })}
              placeholder="Waypoint label (e.g. Belfast Lock)"
              className="flex-1 px-2 py-1 text-sm bg-white border border-gray-200 rounded-md min-h-[36px]"
            />
          </div>
          <input
            type="text"
            value={item.instructionNote || ''}
            onChange={(e) => onUpdate({ instructionNote: e.target.value })}
            placeholder="Optional instruction note"
            className="w-full px-2 py-1 text-xs bg-white border border-gray-200 rounded-md min-h-[32px]"
          />
        </div>
      ) : (
        <div className="flex-1 min-w-0 px-2 py-1 text-sm">
          <div className="font-medium text-gray-900 truncate flex items-center gap-1">
            <ClipboardList className="h-3.5 w-3.5 flex-shrink-0 text-violet-600" />
            {resolvedOperationLabel}
          </div>
          {operationMissing ? (
            <div className="text-xs text-amber-600 mt-1">
              This operation has been removed. Tap trash to remove, or edit name.
            </div>
          ) : null}
        </div>
      )}

      {/* Trash */}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove item"
        className="p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-red-500 flex-shrink-0 flex items-center justify-center"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
};

// --- VoyagePlanEditor — single-plan edit view ---

const VoyagePlanEditor = ({
  planId,
  onBack,
  voyageSettings,
  plans,
  updatePlan,
  addItem,
  updateItem,
  removeItem,
  moveItem,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const plan = plans.find((p) => p.id === planId) || null;

  if (!plan) {
    return (
      <div className="p-4">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-cyan-600 inline-flex items-center gap-1 min-h-[44px]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to plans
        </button>
        <p className="mt-4 text-sm text-gray-500 italic">
          Plan not found. It may have been deleted in another tab.
        </p>
      </div>
    );
  }

  const items = Array.isArray(plan.items) ? plan.items : [];
  const itemCount = items.length;

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-cyan-600 inline-flex items-center gap-1 min-h-[44px] mb-3"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to plans
      </button>

      <div className="mb-3">
        <label className="block text-xs text-gray-500 mb-1">Plan name</label>
        <input
          type="text"
          value={plan.name || ''}
          onChange={(e) => updatePlan(plan.id, { name: e.target.value })}
          placeholder="e.g. Belfast → Bangor"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md min-h-[44px] focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
      </div>

      {/* Default From / To — auto-fill Route on the Master's Start Voyage form */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Default From</label>
          <input
            type="text"
            value={plan.defaultFrom || ''}
            onChange={(e) => updatePlan(plan.id, { defaultFrom: e.target.value })}
            placeholder="e.g. Belfast"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md min-h-[44px] focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Default To</label>
          <input
            type="text"
            value={plan.defaultTo || ''}
            onChange={(e) => updatePlan(plan.id, { defaultTo: e.target.value })}
            placeholder="e.g. Bangor"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md min-h-[44px] focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
      </div>

      {/* Default Purpose — auto-fill Voyage Purpose on the Master's Start Voyage form */}
      <div className="mb-3">
        <label className="block text-xs text-gray-500 mb-1">Default purpose</label>
        <Dropdown
          value={plan.defaultPurpose || ''}
          onChange={(value) => updatePlan(plan.id, { defaultPurpose: value })}
          options={[
            { value: '', label: 'No default' },
            ...(Array.isArray(voyageSettings?.purposesList) ? voyageSettings.purposesList : [])
              .filter((p) => p && p.enabled !== false)
              .map((p) => ({ value: p.id, label: p.label }))
          ]}
          width="w-full"
        />
      </div>

      <p className="text-xs text-gray-400 mb-4">
        Default From / To / Purpose pre-fill the Master's Start Voyage form when they pick this plan. All optional — leave blank to let the Master fill them in.
      </p>

      <div className="mb-2 flex items-center justify-between">
        <h5 className="text-sm font-medium text-gray-900">
          Items {itemCount > 0 ? `(${itemCount})` : ''}
        </h5>
      </div>

      {itemCount === 0 ? (
        <p className="text-sm text-gray-400 italic mb-3 p-2">
          No items yet. Add waypoints and operation references below.
        </p>
      ) : (
        <ul className="space-y-2 mb-3">
          {items.map((item, idx) => (
            <PlanItemRow
              key={item.id}
              item={item}
              position={idx + 1}
              isFirst={idx === 0}
              isLast={idx === items.length - 1}
              voyageSettings={voyageSettings}
              onUpdate={(patch) => updateItem(plan.id, item.id, patch)}
              onMoveUp={() => moveItem(plan.id, item.id, 'up')}
              onMoveDown={() => moveItem(plan.id, item.id, 'down')}
              onRemove={() => removeItem(plan.id, item.id)}
            />
          ))}
        </ul>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => addItem(plan.id, { kind: 'waypoint', label: 'New waypoint' })}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-md hover:bg-cyan-100 min-h-[44px]"
        >
          <MapPin className="h-4 w-4" />
          Add Waypoint
        </button>
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-md hover:bg-violet-100 min-h-[44px]"
        >
          <ClipboardList className="h-4 w-4" />
          Add Operation
        </button>
      </div>

      {showPicker ? (
        <OperationRefPicker
          voyageSettings={voyageSettings}
          onPick={(operationId) => {
            addItem(plan.id, { kind: 'operation_ref', operationId });
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      ) : null}
    </div>
  );
};

// --- VoyagePlansList — list view with active + archived sections ---

const VoyagePlansList = ({
  activePlans,
  archivedPlans,
  onEdit,
  onCreate,
  onArchive,
  onUnarchive,
  onDelete,
}) => {
  const [showArchived, setShowArchived] = useState(false);

  const activeCount = activePlans.length;
  const archivedCount = archivedPlans.length;

  // First-use empty state — no active AND no archived.
  if (activeCount === 0 && archivedCount === 0) {
    return <EmptyState onCreate={onCreate} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          {activeCount === 0
            ? 'No active plans'
            : `${activeCount} ${activeCount === 1 ? 'plan' : 'plans'}`}
          {archivedCount > 0 ? ` (${archivedCount} archived)` : ''}
        </p>
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 min-h-[44px]"
        >
          <Plus className="h-4 w-4" />
          New plan
        </button>
      </div>

      {activeCount === 0 ? (
        <p className="text-sm text-gray-400 italic mb-3 p-2">No active plans.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {activePlans.map((p) => {
            const itemCount = Array.isArray(p.items) ? p.items.length : 0;
            return (
              <li
                key={p.id}
                className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {p.name || 'Untitled plan'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'} · {formatEditedAgo(p.updatedAt)}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => onEdit(p.id)}
                    aria-label={`Edit ${p.name}`}
                    className="p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-cyan-600 flex items-center justify-center"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onArchive(p.id)}
                    aria-label={`Archive ${p.name}`}
                    className="p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-amber-600 flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {archivedCount > 0 ? (
        <div>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 min-h-[44px]"
          >
            {showArchived ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
          </button>
          {showArchived ? (
            <ul className="space-y-2 mt-2">
              {archivedPlans.map((p) => {
                const itemCount = Array.isArray(p.items) ? p.items.length : 0;
                return (
                  <li
                    key={p.id}
                    className="flex items-center justify-between bg-gray-100 rounded-md px-3 py-2 opacity-75"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-700 truncate">
                        {p.name || 'Untitled plan'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {itemCount} {itemCount === 1 ? 'item' : 'items'} · archived
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => onUnarchive(p.id)}
                        aria-label={`Restore ${p.name}`}
                        className="p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-cyan-600 flex items-center justify-center"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // eslint-disable-next-line no-alert
                          if (window.confirm(`Permanently delete "${p.name || 'Untitled plan'}"? This cannot be undone.`)) {
                            onDelete(p.id);
                          }
                        }}
                        aria-label={`Delete ${p.name}`}
                        className="p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-red-600 flex items-center justify-center"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// --- Top-level VoyagePlansStep ---

const VoyagePlansStep = ({ voyageSettings }) => {
  const {
    activePlans,
    archivedPlans,
    plans,
    loaded,
    createPlan,
    updatePlan,
    archivePlan,
    unarchivePlan,
    deletePlan,
    addItem,
    updateItem,
    removeItem,
    moveItem,
  } = useVoyagePlans();

  const [view, setView] = useState({ mode: 'list' });

  // If the currently-edited plan disappears (e.g. archived in another tab),
  // fall back to the list view. Otherwise the editor renders "Plan not found".
  // We don't bounce the user mid-edit, only on next render after the disappearance.
  const lastSeenPlanIdRef = useRef(null);
  useEffect(() => {
    if (view.mode !== 'edit') return;
    const exists = plans.some((p) => p.id === view.planId);
    if (!exists && lastSeenPlanIdRef.current === view.planId) {
      setView({ mode: 'list' });
    }
    if (exists) lastSeenPlanIdRef.current = view.planId;
  }, [view, plans]);

  if (!loaded) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  }

  if (view.mode === 'edit') {
    return (
      <VoyagePlanEditor
        planId={view.planId}
        plans={plans}
        voyageSettings={voyageSettings}
        onBack={() => setView({ mode: 'list' })}
        updatePlan={updatePlan}
        addItem={addItem}
        updateItem={updateItem}
        removeItem={removeItem}
        moveItem={moveItem}
      />
    );
  }

  return (
    <VoyagePlansList
      activePlans={activePlans}
      archivedPlans={archivedPlans}
      onEdit={(id) => setView({ mode: 'edit', planId: id })}
      onCreate={() => {
        const plan = createPlan({ name: 'New plan' });
        setView({ mode: 'edit', planId: plan.id });
      }}
      onArchive={archivePlan}
      onUnarchive={unarchivePlan}
      onDelete={deletePlan}
    />
  );
};

export default VoyagePlansStep;
// Named exports for testing convenience
export {
  EmptyState as __EmptyState,
  OperationRefPicker as __OperationRefPicker,
  PlanItemRow as __PlanItemRow,
  VoyagePlanEditor as __VoyagePlanEditor,
  VoyagePlansList as __VoyagePlansList,
};
